import {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useCallback,
  type ReactNode,
} from "react";

// ============================================================
// Types
// ============================================================

export interface CartItem {
  productId: string;
  variantId: string;
  productName: string;
  variantName: string;
  quantity: number;
  unitPrice: number;
  imageUrl: string;
}

interface CartState {
  items: CartItem[];
}

type CartAction =
  | { type: "ADD_ITEM"; payload: CartItem }
  | { type: "REMOVE_ITEM"; payload: { productId: string; variantId: string } }
  | {
      type: "UPDATE_QUANTITY";
      payload: { productId: string; variantId: string; quantity: number };
    }
  | {
      type: "CHANGE_VARIANT";
      payload: {
        productId: string;
        oldVariantId: string;
        newVariantId: string;
        newVariantName: string;
        newImageUrl: string;
      };
    }
  | { type: "CLEAR_CART" };

interface CartContextValue {
  items: CartItem[];
  itemCount: number;
  totalAmount: number;
  addItem: (item: CartItem) => void;
  removeItem: (productId: string, variantId: string) => void;
  updateQuantity: (
    productId: string,
    variantId: string,
    quantity: number
  ) => void;
  changeVariant: (
    productId: string,
    oldVariantId: string,
    newVariantId: string,
    newVariantName: string,
    newImageUrl: string
  ) => void;
  clearCart: () => void;
}

// ============================================================
// Reducer
// ============================================================

const STORAGE_KEY = "brindes_cart";

function loadCart(): CartItem[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function saveCart(items: CartItem[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // localStorage pode estar cheio ou indisponível
  }
}

function cartReducer(state: CartState, action: CartAction): CartState {
  let newItems: CartItem[];

  switch (action.type) {
    case "ADD_ITEM": {
      const { productId, variantId, quantity } = action.payload;
      const existing = state.items.find(
        (i) => i.productId === productId && i.variantId === variantId
      );

      if (existing) {
        // Atualiza quantidade (soma)
        newItems = state.items.map((i) =>
          i.productId === productId && i.variantId === variantId
            ? { ...i, quantity: i.quantity + quantity }
            : i
        );
      } else {
        newItems = [...state.items, action.payload];
      }
      break;
    }

    case "REMOVE_ITEM": {
      newItems = state.items.filter(
        (i) =>
          !(
            i.productId === action.payload.productId &&
            i.variantId === action.payload.variantId
          )
      );
      break;
    }

    case "UPDATE_QUANTITY": {
      const { productId, variantId, quantity } = action.payload;
      if (quantity <= 0) {
        newItems = state.items.filter(
          (i) => !(i.productId === productId && i.variantId === variantId)
        );
      } else {
        newItems = state.items.map((i) =>
          i.productId === productId && i.variantId === variantId
            ? { ...i, quantity }
            : i
        );
      }
      break;
    }

    case "CHANGE_VARIANT": {
      const { productId, oldVariantId, newVariantId, newVariantName, newImageUrl } =
        action.payload;
      // Se já existe item com a nova variante, somar quantidades
      const existingNew = state.items.find(
        (i) => i.productId === productId && i.variantId === newVariantId
      );
      const oldItem = state.items.find(
        (i) => i.productId === productId && i.variantId === oldVariantId
      );

      if (existingNew && oldItem) {
        // Merge: soma quantidades, remove o antigo
        newItems = state.items
          .filter(
            (i) =>
              !(
                i.productId === productId &&
                (i.variantId === oldVariantId || i.variantId === newVariantId)
              )
          )
          .concat({
            ...existingNew,
            quantity: existingNew.quantity + oldItem.quantity,
            variantName: newVariantName,
            imageUrl: newImageUrl,
          });
      } else if (oldItem) {
        // Só troca variante no item existente
        newItems = state.items.map((i) =>
          i.productId === productId && i.variantId === oldVariantId
            ? { ...i, variantId: newVariantId, variantName: newVariantName, imageUrl: newImageUrl }
            : i
        );
      } else {
        newItems = state.items;
      }
      break;
    }

    case "CLEAR_CART": {
      newItems = [];
      break;
    }

    default:
      return state;
  }

  return { items: newItems };
}

// ============================================================
// Context
// ============================================================

const CartContext = createContext<CartContextValue | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(cartReducer, { items: [] }, () => ({
    items: loadCart(),
  }));

  // Salva no localStorage sempre que muda
  useEffect(() => {
    saveCart(state.items);
  }, [state.items]);

  const addItem = useCallback((item: CartItem) => {
    dispatch({ type: "ADD_ITEM", payload: item });
  }, []);

  const removeItem = useCallback(
    (productId: string, variantId: string) => {
      dispatch({ type: "REMOVE_ITEM", payload: { productId, variantId } });
    },
    []
  );

  const updateQuantity = useCallback(
    (productId: string, variantId: string, quantity: number) => {
      dispatch({
        type: "UPDATE_QUANTITY",
        payload: { productId, variantId, quantity },
      });
    },
    []
  );

  const changeVariant = useCallback(
    (
      productId: string,
      oldVariantId: string,
      newVariantId: string,
      newVariantName: string,
      newImageUrl: string
    ) => {
      dispatch({
        type: "CHANGE_VARIANT",
        payload: { productId, oldVariantId, newVariantId, newVariantName, newImageUrl },
      });
    },
    []
  );

  const clearCart = useCallback(() => {
    dispatch({ type: "CLEAR_CART" });
  }, []);

  const itemCount = state.items.reduce((sum, i) => sum + i.quantity, 0);
  const totalAmount = state.items.reduce(
    (sum, i) => sum + i.unitPrice * i.quantity,
    0
  );

  return (
    <CartContext.Provider
      value={{
        items: state.items,
        itemCount,
        totalAmount,
        addItem,
        removeItem,
        updateQuantity,
        changeVariant,
        clearCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart(): CartContextValue {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart deve ser usado dentro de um CartProvider.");
  }
  return context;
}
